import type { IBoostTAE } from '@boost/widget-integration-tae/lib';
import { Module, ModuleRefImpl, type OnApplicationBootstrap } from 'framework/core';
import { Constructor } from 'framework/di';
import { PerformanceService } from 'framework/services';

import { SESSION_KEY } from '../../@deprecation/constants/app';
import { initAnalytic } from '../../analytic';
import { initApp } from '../../app';
import { AsyncHelper } from '../../helpers/async.helper';
import { templateManagement } from '../../template/template-management';
import { B2BModule } from '../b2b/b2b.module';
import { PlatformLoader } from '../platform/platform.loader';
import { PlatformModule } from '../platform/platform.module';
import { RecentlyViewedProductModule } from '../recently-viewed-product/recently-viewed-product.module';
import { TierDiscountModule } from '../tier-discount/tier-discount.module';
import { AppService } from './app.service';

@Module({
  imports: [PlatformModule, RecentlyViewedProductModule, TierDiscountModule, B2BModule],
  providers: [AppService, PerformanceService, AsyncHelper],
})
export class AppModule implements OnApplicationBootstrap {
  TAEApp: IBoostTAE;
  performanceService: PerformanceService;
  platformLoader: PlatformLoader;
  asyncHelper: AsyncHelper;

  constructor(public moduleRef: ModuleRefImpl) {
    this.performanceService = this.moduleRef.get<PerformanceService>(PerformanceService);
    this.TAEApp = this.moduleRef.get<AppService>(AppService).TAEApp;
    this.platformLoader = this.moduleRef.get<PlatformLoader>(PlatformLoader);
    this.asyncHelper = this.moduleRef.get<AsyncHelper>(AsyncHelper);

    Object.assign(window.boostWidgetIntegration, {
      App: this,
    });
  }

  async onApplicationBootstrap() {
    const { performanceService, TAEApp } = this;
    performanceService.start();

    window.boostWidgetIntegration.generateUuid = function () {
      return 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[x]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        return r.toString(16);
      });
    };

    window.boostWidgetIntegration.getSessionId = function () {
      try {
        let sessionId = localStorage.getItem(SESSION_KEY);

        if (!sessionId) {
          sessionId = window.boostWidgetIntegration.generateUuid();
          localStorage.setItem(SESSION_KEY, sessionId);
        }

        return sessionId;
      } catch {
        return window.boostWidgetIntegration.generateUuid();
      }
    };

    TAEApp.addRegistryBlockListener(async (block) => {
      const { blocks } = TAEApp;

      const loadingModulePromises: Promise<void>[] = [];

      if (blocks[block.id]) {
        if (block.blockType === 'filter') {
          loadingModulePromises.push(
            this.moduleRef.loadModule(
              () =>
                import(
                  /* webpackChunkName: "filter.module" */
                  '../filter/filter.module'
                ).then((m) => m.FilterModule),
              {
                singleton: true,
              }
            )
          );
        }

        if (block.blockType === 'instantSearch') {
          loadingModulePromises.push(
            this.moduleRef.loadModule(
              () =>
                import(
                  /* webpackChunkName: "instant-search.module" */
                  '../instant-search/instant-search.module'
                ).then((m) => m.InstantSearchModule),
              {
                singleton: true,
              }
            )
          );
        }

        if (block.blockType === 'countdownTimer') {
          loadingModulePromises.push(
            this.moduleRef.loadModule(
              () =>
                import(
                  /* webpackChunkName: "countdown-timer.module" */
                  '../countdown-timer/countdown-timer.module'
                ).then((m) => m.CountdownTimerModule),
              {
                singleton: true,
              }
            )
          );
        }

        if (block.blockType === 'recommendation') {
          loadingModulePromises.push(
            this.moduleRef.loadModule(
              () =>
                import(
                  /* webpackChunkName: "recommendation.module" */
                  '../recommendation/recommendation.module'
                ).then((m) => m.RecommendationModule),
              {
                singleton: true,
              }
            )
          );
        }

        if (
          block.blockType === 'preOrder' &&
          this.platformLoader.platform.router.page === 'product'
        ) {
          loadingModulePromises.push(
            this.moduleRef.loadModule(
              () =>
                import(
                  /* webpackChunkName: "pre-order.module" */
                  '../pre-order/pre-order.module'
                ).then((m) => m.PreOrderModule),
              {
                singleton: true,
              }
            )
          );
        }

        if (
          block.blockType === 'backInStock' &&
          this.TAEApp.config.backInStock?.enabled &&
          this.TAEApp.config.backInStock?.isTurbo &&
          this.platformLoader.platform.router.page === 'product'
        ) {
          loadingModulePromises.push(
            this.moduleRef.loadModule(
              () =>
                import(
                  /* webpackChunkName: "back-in-stock.module" */
                  '../back-in-stock/back-in-stock.module'
                ).then((m) => m.BackInStockModule),
              {
                singleton: true,
              }
            )
          );
        }

        if (
          block.blockType === 'predictiveBundle' &&
          this.platformLoader.platform.router.page === 'product'
        ) {
          loadingModulePromises.push(
            this.moduleRef.loadModule(
              () =>
                import(
                  /* webpackChunkName: "predictive-bundle.module" */
                  '../predictive-bundle/predictive-bundle.module'
                ).then((m) => m.PredictiveBundleModule),
              {
                singleton: true,
              }
            )
          );
        }

        if (
          block.blockType === 'volumeBundle' &&
          this.platformLoader.platform.router.page === 'product'
        ) {
          loadingModulePromises.push(
            this.moduleRef.loadModule(
              () =>
                import(
                  /* webpackChunkName: "volume-bundle.module" */
                  '../volume-bundle/volume-bundle.module'
                ).then((m) => m.VolumeBundleModule),
              {
                singleton: true,
              }
            )
          );
        }

        await Promise.all(loadingModulePromises);

        if (blocks[block.id].app) {
          performanceService.measureSync(`Initialize ${block.id} block`, () => {
            // Old initialization method
            initApp(blocks[block.id]);
          });
        }
      }
    });

    this.asyncHelper.requestIdleCallback(() => {
      initAnalytic();
    });

    this.setupCartDrawerCountdownTimerLoader();

    if (Object.keys(TAEApp.blocks).length > 0) {
      setTimeout(() => {
        templateManagement(Object.values(TAEApp.blocks)[0]);
      }, 30000);
    }
  }

  /**
   * Ensure the CountdownTimerModule is loaded when the cart drawer opens,
   * even if the current page has no countdownTimer TAE block.
   *
   * If the module was already loaded by a `countdownTimer` block on this
   * page, its own listener handles the event directly.
   * If the module was NOT loaded yet, we load it and re-dispatch the
   * event so the freshly-registered listener picks it up.
   */
  private setupCartDrawerCountdownTimerLoader(): void {
    let moduleLoadedByBlock = false;

    // Track whether the block listener already loaded the module
    this.TAEApp.addRegistryBlockListener((block) => {
      if (block.blockType === 'countdownTimer') {
        moduleLoadedByBlock = true;
      }
    });

    window.addEventListener(
      'boost-sd-cart-drawer-opened',
      async () => {
        if (moduleLoadedByBlock) return;

        await this.moduleRef.loadModule(
          () =>
            import(
              /* webpackChunkName: "countdown-timer.module" */
              '../countdown-timer/countdown-timer.module'
            ).then((m) => m.CountdownTimerModule),
          {
            singleton: true,
          }
        );
        // The module just registered its own listener, but it missed
        // the event that triggered this load — re-dispatch so it picks up.
        window.dispatchEvent(new CustomEvent('boost-sd-cart-drawer-opened'));
      },
      { once: true }
    );
  }
}

export function getInstance<Module>(module: Constructor<Module>): Module | null {
  const app = window.boostWidgetIntegration.App as AppModule;
  return app?.moduleRef?.get(module) ?? null;
}

export function getAppModuleRef(): ModuleRefImpl {
  const app = window.boostWidgetIntegration.App as AppModule;
  return app.moduleRef;
}
